import { runInAction } from 'mobx'
import _ from 'lodash'
import moment from 'moment'

// evertel
import { Api } from '@evertel/api'
import { decorate, inject, injectable } from '@evertel/di'
import { APIDataBlueUser } from '@evertel/types'
import { DepartmentsAccessStore } from '@evertel/departments-access'
import { BlueUserStore } from '../store'

export const DEFAULT_USER_SCOPE = {
    fields: ['id', 'firstName', 'lastName', 'publicImage', 'publicMedia', 'isBot', 'isAdmin', 'emailVerified', 'email', 'phoneNumber', 'signupDate', 'reportingDepartment'],
    include: [{
        relation: 'departmentsAccess',
        scope: {
            fields: ['id', 'isVerified', 'roles', 'departmentId', 'positionString', 'isPrimary'],
            include: [
                {
                    relation: 'department',
                    scope: {
                        fields: ['id', 'name', 'shortName', 'city', 'state']
                    }
                }
            ]
        }
    }]
}

// A service to optimize and minimize how often we fetch user profiles
class FetchUserService {
    private recentlyFetched: Map<number, moment.Moment> = new Map()

    constructor(
        private api: Api,
        private userStore: BlueUserStore,
        private departmentAccessStore: DepartmentsAccessStore
    ) {
        // makeAutoObservable(this)
    }

    private isRecentAndInStore = (userId: number): boolean => {
        if (!this.userStore.findById(userId)) return false
        const lastFetch = this.recentlyFetched.get(userId)
        if (!lastFetch) return false
        if (lastFetch < moment().subtract(5, 'minutes')) return false
        return true
    }

    // Returns a list of id's filtered by recently fetched id's
    private filterIdsByRecentlyFetched = (userIds: number[]) => {
        return userIds.filter(id => !this.isRecentAndInStore(id))
    }

    private wasRecentlyFetched = (userId: number) => {
        return this.isRecentAndInStore(userId)
    }

    addRecentlyFetchedUsers = (users: APIDataBlueUser[]) => {
        const lastFetch = moment()

        this.userStore.update(users)

        const userIdSet = new Set(users.map(u => u.id))

        const staleIds = this.departmentAccessStore
            .find(da => userIdSet.has(da.blueUserId))
            .map(da => da.id)

        const departmentsAccesses = users.flatMap(u => u.departmentsAccess ?? [])

        runInAction(() => {
            // Remove stale entries for these users
            this.departmentAccessStore.deleteByIds(staleIds)
            // Insert fresh data
            this.departmentAccessStore.update(departmentsAccesses)
        })

        // TODO: Store user.departmentsAccess[].department in DepartmentStore OR remove the relation 'department' from defaultUserScope

        for (const u of users) {
            this.recentlyFetched.set(u.id, lastFetch)
        }
    }

    addRecentlyFetchedUser = (user: APIDataBlueUser) => {
        this.addRecentlyFetchedUsers([user])
    }

    removeRecentlyFetchedUser = (userId: number) => {
        this.userStore.deleteById(userId)
        this.recentlyFetched.delete(userId)
    }

    async fetchCurrentUser(userId: number): Promise<APIDataBlueUser | undefined> {
        if (!userId) return undefined
        if (this.wasRecentlyFetched(userId)) return this.userStore.findById(userId)

        // small hack, when this function gets called multiple times, we don't get a response early enough to avoid
        // the same API call to be repeated.
        this.addRecentlyFetchedUser({ id: userId })
        try {
            const usersFetched = await this.api.Routes.BlueUser.getById(userId)
            this.addRecentlyFetchedUser(usersFetched)
        } catch (e) {
            this.removeRecentlyFetchedUser(userId)
        }
        return this.userStore.findById(userId)
    }

    async fetchUserAsAdmin(userId: number): Promise<APIDataBlueUser | undefined> {
        if (!userId) return undefined

        const userFetched = await this.api.Routes.BlueUser.getById(userId, {
            include: [{
                relation: 'publicMedia'
            }, {
                relation: 'departmentsAccess',
                scope: {
                    include: [{
                        relation: 'department',
                        scope: {
                            include: ['publicMedia']
                        }
                    }]
                }
            }]
        })

        this.addRecentlyFetchedUsers([userFetched])

        return this.userStore.findById(userId)
    }

    async fetchSpecificUsersFromRoom(roomId: number, ids: number[]): Promise<APIDataBlueUser[]> {
        const lessIds = this.filterIdsByRecentlyFetched(ids)

        if (!roomId) return []
        if (!lessIds.length) return this.userStore.findByIds(ids)

        // TODO: we need to add support for pagination, this rout returns a maximum of 100 users
        if (lessIds.length > 100) throw Error('TODO add support for a limit of 100')

        const usersFetched = await this.api.Routes.Room.getUsers(roomId, { where: { id: { inq: lessIds} }, ...DEFAULT_USER_SCOPE })

        this.addRecentlyFetchedUsers(usersFetched)

        return this.userStore.findByIds(ids)
    }


    async fetchSpecificUsersFromThread(threadId: number, ids: number[]): Promise<APIDataBlueUser[]> {
        const lessIds = this.filterIdsByRecentlyFetched(ids)

        if (!threadId) return []
        if (!lessIds.length) return this.userStore.findByIds(ids)

        // TODO: we need to add support for pagination, this rout returns a maximum of 100 users
        if (lessIds.length > 100) throw Error('TODO add support for a limit of 100')

        const usersFetched = await this.api.Routes.Thread.getUsers(threadId, { where: { id: { inq: lessIds} }, ...DEFAULT_USER_SCOPE })
        this.addRecentlyFetchedUsers(usersFetched)

        return this.userStore.findByIds(ids)
    }

    async fetchSpecificUsersFromDepartment(departmentId: number, ids: number[]): Promise<APIDataBlueUser[]> {
        const lessIds = this.filterIdsByRecentlyFetched(ids)

        if (!departmentId) return []
        if (!lessIds.length) return this.userStore.findByIds(ids)

        // TODO: we need to add support for pagination, this rout returns a maximum of 100 users
        if (lessIds.length > 100) throw Error('TODO add support for a limit of 100')

        const usersFetched = await this.api.Routes.Department.getUsers(departmentId, { where: { id: { inq: lessIds} }, ...DEFAULT_USER_SCOPE })
        this.addRecentlyFetchedUsers(usersFetched)

        return this.userStore.findByIds(ids)
    }

    async fetchSpecificUserFromDepartment(departmentId: number, id: number): Promise<APIDataBlueUser | undefined> {
        const lessIds = this.filterIdsByRecentlyFetched([id])

        if (!departmentId) return undefined
        if (!lessIds.length) return this.userStore.findById(id)

        //TODO: This endpoint ignores the filter, fix it
        const userFetched = await this.api.Routes.Department.getUserById(departmentId, id, DEFAULT_USER_SCOPE)
        this.addRecentlyFetchedUsers([userFetched])

        return this.userStore.findById(id)
    }

    async fetchUsersFromThreads(userIds: number[]): Promise<APIDataBlueUser[]> {
        const lessIds = this.filterIdsByRecentlyFetched(userIds)

        if (!lessIds.length) return this.userStore.findByIds(userIds)

        const chunks = _.chunk(lessIds, 100)
        const results = await Promise.all(
            chunks.map(chunk => this.api.Routes.Thread.getThreadsUsers(chunk, DEFAULT_USER_SCOPE))
        )
        const usersFetched = results.flat()
        this.addRecentlyFetchedUsers(usersFetched)

        return this.userStore.findByIds(userIds)
    }

    async fetchSpecificUserFromDocument(documentId: number, id: number): Promise<APIDataBlueUser | undefined> {
        if (!this.wasRecentlyFetched(id)) {
            const user = await this.api.Routes.Document.getUserById(documentId, id, DEFAULT_USER_SCOPE)
            this.addRecentlyFetchedUser(user)
        }
        return this.userStore.findById(id)
    }

    destroy = () => {
        this.recentlyFetched.clear()
    }

}

decorate(injectable(), FetchUserService)
decorate(inject(Api), FetchUserService, 0)
decorate(inject(BlueUserStore), FetchUserService, 1)
decorate(inject(DepartmentsAccessStore), FetchUserService, 2)

export { FetchUserService }
